import { useMemo, useState } from 'react';
import { H5, InputGroup } from '@blueprintjs/core';
import { Pagination } from '@gonfalon/pagination/component';

import { DefaultCallout, Module, ModuleContent, Table } from '../../../shared';
import { Box, Flex } from '../../../shared/components';
import { type CustomRole } from '../../model';
import { CustomRoleDetails } from '../CustomRoleDetails';

import { useClientPagination } from './useClientPagination';

import './TeamsPanel.scss';

type CustomRolesPanelProps = {
  customRoles: CustomRole[];
};

const PAGE_SIZE = 20;

export const CustomRolesPanel = ({ customRoles }: CustomRolesPanelProps) => {
  const [roleSearch, setRoleSearch] = useState('');
  const [roleSortDirection, setRoleSortDirection] = useState<'asc' | 'desc'>('asc');

  const normalizedSearch = roleSearch.trim().toLowerCase();
  const filteredRoles = useMemo(() => {
    if (!normalizedSearch) {
      return customRoles;
    }

    return customRoles.filter((role) => {
      const nameMatch = role.name.toLowerCase().includes(normalizedSearch);
      const keyMatch = role.key.toLowerCase().includes(normalizedSearch);
      return nameMatch || keyMatch;
    });
  }, [customRoles, normalizedSearch]);

  const sortedRoles = useMemo(() => {
    const sorted = [...filteredRoles];
    sorted.sort((a, b) => {
      const delta = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      return roleSortDirection === 'asc' ? delta : -delta;
    });
    return sorted;
  }, [filteredRoles, roleSortDirection]);

  const {
    pageItems: paginatedRoles,
    totalCount: filteredRolesCount,
    from,
    to,
    hasPreviousPage,
    hasNextPage,
    gotoFirstPage,
    gotoPreviousPage,
    gotoNextPage,
    gotoLastPage,
  } = useClientPagination({
    items: sortedRoles,
    pageSize: PAGE_SIZE,
    resetKey: `${normalizedSearch}:${roleSortDirection}`,
  });

  const totalRoles = customRoles.length;
  const showPagination = filteredRolesCount > PAGE_SIZE;

  const toggleRoleSort = () => {
    setRoleSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const renderSortIndicator = () => (
    <span className="rolesAndTeams__sortIndicator">{roleSortDirection === 'asc' ? '▲' : '▼'}</span>
  );

  if (customRoles.length === 0) {
    return <DefaultCallout>No custom roles found for this account.</DefaultCallout>;
  }

  return (
    <Box p={2}>
      <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" className="rolesAndTeams__toolbar">
        <Box>
          <H5>
            {normalizedSearch
              ? `${filteredRolesCount} of ${totalRoles} ${totalRoles === 1 ? 'role' : 'roles'}`
              : `${filteredRolesCount} ${filteredRolesCount === 1 ? 'role' : 'roles'} found`}
          </H5>
          {normalizedSearch && (
            <div className="rolesAndTeams__results">Showing roles matching "{roleSearch.trim()}"</div>
          )}
        </Box>
        <InputGroup
          leftIcon="search"
          placeholder="Search roles..."
          value={roleSearch}
          onChange={(event) => setRoleSearch(event.target.value)}
          className="rolesAndTeams__search"
        />
      </Flex>
      <Module>
        <ModuleContent snug>
          <Table className="rolesAndTeams__table rolesAndTeams__table--roles">
            <colgroup>
              <col className="rolesAndTeams__col rolesAndTeams__col--name" />
              <col className="rolesAndTeams__col rolesAndTeams__col--actions" />
            </colgroup>
            <thead>
              <tr>
                <td>
                  <button type="button" className="rolesAndTeams__sortButton" onClick={toggleRoleSort}>
                    Name {renderSortIndicator()}
                  </button>
                </td>
                <td>Actions</td>
              </tr>
            </thead>
            <tbody>
              {paginatedRoles.length === 0 ? (
                <tr>
                  <td colSpan={2} className="bp4-text-muted">
                    No roles match your search.
                  </td>
                </tr>
              ) : (
                paginatedRoles.map((role) => (
                  <tr key={role.key}>
                    <td>
                      <strong>{role.name}</strong>
                    </td>
                    <td>
                      <CustomRoleDetails role={role} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </ModuleContent>
      </Module>
      {showPagination && (
        <Flex justifyContent="flex-end" className="rolesAndTeams__pagination">
          <Pagination
            from={from}
            to={to}
            total={filteredRolesCount}
            onFirstClick={hasPreviousPage ? gotoFirstPage : undefined}
            onPreviousClick={hasPreviousPage ? gotoPreviousPage : undefined}
            onNextClick={hasNextPage ? gotoNextPage : undefined}
            onLastClick={hasNextPage ? gotoLastPage : undefined}
          />
        </Flex>
      )}
    </Box>
  );
};
